/**
 * Ask layer, interaction log (the settle window) and the telemetry seam.
 */
import { editDistanceWithin, fuzzyBudget } from "./search";

// ---------------------------------------------------------------------------- telemetry seam

export type AssistantEventName = "AssistantInteraction" | "AssistantFeedback";

/**
 * Where the two product events go. Product telemetry — events about what USERS did, as opposed to
 * logs about what the server did — belongs in a table you can filter, group and trend, not in a
 * log stream: the miss log is the catalog's steering instrument, and an instrument whose output
 * reads as "no activity" when the pipe is broken is worse than none.
 *
 * `properties` are dimensions (filter, group by); `measurements` are numbers you average and take
 * percentiles of. Most backends split them — a score stuffed into a string dimension needs a cast
 * in every query that touches it. `null`/`undefined` values are already dropped before the sink
 * sees them, so "unstated" is ABSENT, never `"null"`: a query for "helpful is set" then means
 * "the user gave a verdict", which the feedback event depends on.
 */
export interface TelemetrySink {
  track(
    event: AssistantEventName,
    properties: Record<string, unknown>,
    measurements?: Record<string, number>,
    /** One human line for a local log, when the sink has one. */
    summary?: string
  ): void;
}

const consoleSink: TelemetrySink = {
  track: (event, properties, measurements, summary) => {
    console.info(`[assistant] ${summary ?? event}`, { event, ...properties, ...measurements });
  },
};

let sink: TelemetrySink = consoleSink;

/** Call once at bootstrap to send the events somewhere other than the console — e.g. `setTelemetrySink({ track: (n, p, m) => appInsights.trackEvent({ name: n, properties: p, measurements: m }) })`. */
export const setTelemetrySink = (next: TelemetrySink): void => {
  sink = next;
};

export interface ProductEvent {
  /** Stable name — renaming one breaks every saved query and the history behind it. */
  name: AssistantEventName;
  /** Filterable dimensions. `undefined` and `null` entries are dropped, not sent as "null". */
  properties?: Record<string, string | number | boolean | null | undefined>;
  /** Aggregatable numbers. */
  measurements?: Record<string, number | null | undefined>;
  /** One line for the local log, where there is no telemetry backend to read. */
  summary?: string;
}

const definedProperties = (
  input: Record<string, string | number | boolean | null | undefined> = {}
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === null || value === undefined) continue;
    out[key] = value;
  }
  return out;
};

const definedNumbers = (
  input: Record<string, number | null | undefined> = {}
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === null || value === undefined || Number.isNaN(value)) continue;
    out[key] = value;
  }
  return out;
};

/** Never throws. Telemetry failing must not fail the request that produced it. */
export const emitProductEvent = (event: ProductEvent): void => {
  try {
    sink.track(
      event.name,
      definedProperties(event.properties),
      definedNumbers(event.measurements),
      event.summary
    );
  } catch {
    /* a telemetry row is never worth a failed request */
  }
};

// ---------------------------------------------------------------------------- the settle window

/**
 * One question asked = one logged interaction.
 *
 * The panel searches as you type (500 ms debounce), which is right for the user and was wrong for
 * the instrument: every pause mid-word produced its own `AssistantInteraction`, and since a
 * half-typed word matches nothing, it produced a *miss*. The first real report showed it plainly —
 * `new o` at 12:13:59.1 and `new ordr` at 12:14:00.2, `who has not paiid` → `who has not paid`
 * inside seven seconds. Eight "unanswered questions" were three people-questions.
 *
 * That is not a cosmetic distortion. It inflates the miss rate asymmetrically — a finished
 * question is logged once, an unfinished one once per pause — so the headline metric drifted with
 * typing speed, and the gap report ranked the words users *typed through* above the ones they
 * actually asked.
 *
 * So the event is held for a settle window and emitted only if nothing supersedes it. Held here
 * and not in the panel for two reasons: the client cannot know at request time whether it is the
 * last request of a burst, and telemetry the client decides to send is telemetry the client can
 * get wrong. The request count is unchanged — search still answers every keystroke burst.
 *
 * `queryId` still joins across events: the panel keeps the queryId of its LAST response, which is
 * exactly the one that survives superseding, so an `AssistantFeedback` beacon lands on the
 * interaction that was logged. And to click anything the user must stop typing, which flushes the
 * event first — the window is shorter than any reading.
 *
 * Deliberately not durable: a process recycle drops at most one un-settled event. A queue with
 * delivery guarantees for a telemetry row that says "somebody typed" would cost more than the
 * question it answers.
 */

/**
 * How long a question must stand unchanged before it counts as asked. Five times the panel's
 * search debounce: long enough to bridge a pause for thought mid-question, short enough that a
 * user who reads an answer has always been logged before they can act on it.
 */
export const SETTLE_MS = 2500;

/**
 * Typing, or a new question? Two shapes count as the same question continuing, and nothing else —
 * a rule that guesses wrong deletes a real question from the only report that would have found it.
 *
 *   - **One is a prefix of the other**, in either direction: `new o` → `new ordr` is typing
 *     forward, `new order` → `new ord` is deleting a mistyped tail. Both are the same question.
 *   - **Only the word in progress differs**, within the same edit budget retrieval uses to correct
 *     spelling: `invoicce` → `invoicces`, `who has not paiid` → `who has not paid`. A correction is
 *     not a prefix of what it corrected — the user backspaced into the middle of a word — which is
 *     why prefix matching alone left half the noise in the log.
 *
 * Comparing the two questions as whole strings was the first attempt and it was too loose:
 * "add an order" and "add an offer" are two edits apart, so a sentence-wide budget of two collapsed
 * two different questions into one. Requiring every earlier word to match exactly is what makes
 * this a signature of typing rather than a similarity score — the tail is where a keyboard is.
 *
 * It errs the safe way. Editing the MIDDLE of a question — moving the cursor back to insert a word —
 * is neither a prefix nor a change to the last word, so it is logged as its own question. That
 * leaves a partial row in the log occasionally; the alternative errs towards deleting real
 * questions, and a miss log that loses questions is worth less than one with noise in it.
 */
export const isContinuedTyping = (previous: string, next: string): boolean => {
  const normalise = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, " ");
  const a = normalise(previous);
  const b = normalise(next);
  if (a === b) return true;
  if (a.startsWith(b) || b.startsWith(a)) return true;

  const aWords = a.split(" ");
  const bWords = b.split(" ");
  if (aWords.length !== bWords.length) return false;

  const aTail = aWords.pop() ?? "";
  const bTail = bWords.pop() ?? "";
  if (aWords.join(" ") !== bWords.join(" ")) return false;

  return editDistanceWithin(aTail, bTail, fuzzyBudget(Math.max(aTail.length, bTail.length)));
};

interface Pending {
  question: string;
  event: ProductEvent;
  /** `number` in a browser, a Timeout object in Node — the core compiles under both. */
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Keyed by user, NOT by user+screen: a question typed while the panel is open survives a
 * navigation, and keying on the screen would log the same half-question once per screen. With no
 * user (the panel is behind auth, so this is a fallback) everything shares one key — a collision
 * needs two anonymous callers typing related questions within the same 2.5 s.
 */
const pending = new Map<string, Pending>();

const emitNow = (held: Pending): void => {
  clearTimeout(held.timer);
  emitProductEvent(held.event);
};

export const interactionKey = (userId?: string, tenantId?: string | number | null): string =>
  `${userId ?? "anon"}|${tenantId ?? "-"}`;

/**
 * Hold this interaction for the settle window. A related question arriving inside it replaces the
 * held one (the earlier keystrokes were never a question); an unrelated one lets the held event
 * through, because two questions asked in quick succession are two questions.
 */
export const recordInteraction = (input: {
  key: string;
  question: string;
  event: ProductEvent;
}): void => {
  const held = pending.get(input.key);
  if (held) {
    pending.delete(input.key);
    if (isContinuedTyping(held.question, input.question)) clearTimeout(held.timer);
    else emitNow(held);
  }

  const timer = setTimeout(() => {
    const settled = pending.get(input.key);
    if (!settled) return;
    pending.delete(input.key);
    emitProductEvent(settled.event);
  }, SETTLE_MS);
  // Never hold a Node process open for a telemetry row. Browsers return a number here, which has
  // no `unref` — hence the optional call behind a cast.
  (timer as { unref?: () => void }).unref?.();

  pending.set(input.key, { question: input.question, event: input.event, timer });
};

/** Emit everything still held. For tests, and for any future shutdown hook. */
export const flushInteractions = (): void => {
  for (const [key, held] of Array.from(pending)) {
    pending.delete(key);
    emitNow(held);
  }
};
